import {
	PropsWithChildren,
	createContext,
	startTransition,
	useContext,
	useEffect,
	useState,
} from 'react';

import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	UserProfile,
	type ExtendedProfileType,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import { Cookie } from './cookie';
import { useSSRLoginState } from './ssr-login-state-provider';

export type LoggedInUser = Partial<UserProfile> & {
	isUserLoggedIn: boolean;
	requestInProgress: boolean;
	extended_profile?: ExtendedProfileType;
};

const DEFAULT_USER_STATE: LoggedInUser = {
	isUserLoggedIn: false,
	requestInProgress: true,
};

/**
 * Reads the login cookies directly. Only safe where a render mismatch cannot
 * occur (i.e. in effects, or on the static non-SSR path), because the server
 * cannot see these cookies from JS during render.
 */
const hasLoginCookie = (): boolean => {
	const loginUserDetected = Cookie.readCookie('login_user_detected') === 'true';
	const cloudSessionToken =
		Cookie.readCookie('cloud.session.token') || Cookie.readCookie('cloud.session.token.stg');

	// Boolean(), not `!== null`: readCookie returns '' for a cookie that is present
	// but empty, which would otherwise read as a live session.
	return loginUserDetected || Boolean(cloudSessionToken);
};

/**
 * The value `requestInProgress` must start with.
 *
 * When the page was server-rendered, this has to match what the server rendered.
 * The server decides from `hasCloudSessionTokenCookie`, so the client must start
 * from that same flag rather than from cookies — otherwise the two disagree, the
 * user-profile markup changes shape mid-hydration, and React throws the whole
 * server tree away and re-renders the root from scratch.
 *
 * Without SSR (static fallback, Storybook, tests) there is no server markup to
 * match, so cookies can be read directly for a more accurate first paint.
 */
const getInitialRequestInProgress = (
	ssrHasSessionCookie: boolean,
	isResolvedBySSR: boolean,
): boolean => (isResolvedBySSR ? ssrHasSessionCookie : hasLoginCookie());

const LoggedInUserContext = createContext<LoggedInUser>({
	...DEFAULT_USER_STATE,
});

/**
 * Custom hook to return LoggedInUserContext.
 * Returns `DEFAULT_USER_STATE` by default until the user profile is fetched via `stargateClient.me()`.
 * @returns @type {LoggedInUser}
 */
export const useLoggedInUser = (): LoggedInUser => {
	const context = useContext(LoggedInUserContext);
	if (!context) {
		throw new Error('calls to useLoggedInUser must be wrapped in <LoggedInUserProvider>');
	}

	return context;
};

export const LoggedInUserProvider = ({ children }: PropsWithChildren) => {
	const stargateClient = useBxpWebStargateClient();
	const { hasCloudSessionTokenCookie, isResolvedBySSR } = useSSRLoginState();
	const [loggedInUserInstance, setLoggedInUserInstance] = useState<LoggedInUser>(() => ({
		...DEFAULT_USER_STATE,
		requestInProgress: getInitialRequestInProgress(hasCloudSessionTokenCookie, isResolvedBySSR),
	}));

	useEffect(() => {
		startTransition(() => {
			// Check for login_user_detected cookie first (set during SSR).
			// As fallback, check for cloud.session.token directly (handles static fallback case).
			const isUserLoggedIn = hasLoginCookie();

			if (!isUserLoggedIn) {
				// If logged in user cookie isn't present, then we don't need to make a call to stargate.
				// This cookie is set by bxp-gateway to determine if the user is logged in or not.
				//
				// Return the existing object unchanged when we already settled on
				// "logged out" during the initial render. React bails out of the
				// re-render on an identical reference, which keeps this effect from
				// mutating a tree that may still be hydrating.
				setLoggedInUserInstance((current) =>
					current.requestInProgress || current.isUserLoggedIn
						? { ...DEFAULT_USER_STATE, requestInProgress: false }
						: current,
				);
				return;
			}

			const getUserProfile = async () => {
				return await stargateClient.me({});
			};

			getUserProfile()
				.then((userProfile) => {
					setLoggedInUserInstance({
						requestInProgress: false,
						isUserLoggedIn: true,
						...userProfile,
					});
				})
				.catch((e) => {
					console.warn('Error getting user profile: ', e);
					setLoggedInUserInstance({
						...DEFAULT_USER_STATE,
						requestInProgress: false,
					});
				});
		});
	}, [stargateClient]);

	return (
		<LoggedInUserContext.Provider value={loggedInUserInstance}>
			{children}
		</LoggedInUserContext.Provider>
	);
};

type MockLoggedInUserProviderProps = PropsWithChildren & {
	loggedInUser: LoggedInUser;
};

export const MockLoggedInUserProvider = ({
	children,
	loggedInUser,
}: MockLoggedInUserProviderProps) => (
	<LoggedInUserContext.Provider value={loggedInUser}>{children}</LoggedInUserContext.Provider>
);
